import { ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
	AppButton,
	Collapse,
	Divider,
	DotMatrix,
	Input,
	ListItem,
	MenuList,
	MenuListDivider,
	Modal,
	PANEL_GAP,
	SegmentedTab,
	SegmentedTabList,
	SegmentedTabs,
	StatusDot,
	Surface,
	Toggle,
	anchoredRight,
	anchoredTop,
	useAnchoredPlacement,
	useEnterExit,
	useMenuRoomFit,
	usePopoverMotion
} from '@clickguard/design-system'
import AlertCircle from '@untitled-ui/icons-react/build/esm/AlertCircle'
import AnnotationPlus from '@untitled-ui/icons-react/build/esm/AnnotationPlus'
import CheckCircle from '@untitled-ui/icons-react/build/esm/CheckCircle'
import ChevronDown from '@untitled-ui/icons-react/build/esm/ChevronDown'
import Plus from '@untitled-ui/icons-react/build/esm/Plus'
import RefreshCcw01 from '@untitled-ui/icons-react/build/esm/RefreshCcw01'
import Settings01 from '@untitled-ui/icons-react/build/esm/Settings01'
import {
	useFormSpam,
	BLOCK_THRESHOLDS,
	BOT_SENSITIVITIES,
	BlockThreshold,
	BotSensitivity,
	CrmAccount,
	CrmProvider,
	EmailSetting,
	SETUP_WARN,
	setEmailMaster,
	setEmailRule
} from '../form-spam-data'
import {
	BrevoMark,
	CONFIRM_MODAL_WIDTH,
	CRM_NAMES,
	CrmFieldPanel,
	CrmFieldState,
	CrmRow,
	CrmState,
	DialogHeader,
	EmailStep,
	HubSpotMark,
	InsetList,
	RequestState,
	SettingsCard,
	ToggleRow,
	WidgetCheckState,
	WidgetStep
} from './form-settings-shared'

/**
 * R262 (Esteban, 3 Sep): "mirror whats going on in the modal here in the setup
 * drawer / ofc not the same, no left pane and such, but the content yeah".
 *
 * The four wizard pages (R235) used to live inside `link-form-cards-modal.tsx`
 * as inline JSX over the modal's local state. The drawer's Setup tab drew its
 * own older cards (`EmailStep` with a master row, `CrmStep showHeader`, the
 * three-level `BotStep`), so the two surfaces had drifted apart. This module
 * holds the page CONTENT once, as four panes with plain props, and both hosts
 * render them: the wizard with its draft state behind a rail and a footer, the
 * drawer with the record behind nothing. What moved here from the modal file
 * is a move, not a copy: `CARDS`, the tile map, `fold`, `PaneHeader`, `Dim`,
 * the connected-CRM card and the URL check. The rail, the footer and the page
 * walk stay in the modal.
 *
 * Every pane keeps the wizard's motion: the picker and the connected card fold
 * over 200ms, a dimmed body fades over 200ms, the status line crossfades, and
 * every fold is a mounted element that leaves before it unmounts.
 */

export type SubKey = 'email' | 'crm' | 'bot'
export type VerifyState = 'idle' | 'checking' | 'detected' | 'missing'
type Strategy = 'absolute' | 'fixed'

/**
 * The three protections, in wizard order. `title` and `desc` are the R182 card
 * copy, untouched (R235 assumption 2). `label` is the rail's substep row.
 */
export const CARDS: { key: SubKey; title: string; label: string; desc: string }[] = [
	{
		key: 'email',
		title: 'Block spammers from seeing my PPC Ads',
		label: 'Protect my ads',
		desc: 'Prevent your ads from being shown again to users that submit invalid emails'
	},
	{
		key: 'crm',
		title: 'Flag invalid submissions on my CRM',
		// R255 (Esteban, 3 Sep, Inspect edit): the rail row reads shorter than the pane title.
		label: 'Flag submissions on CRM',
		desc: 'Integrate ClickGuard to your CRM and flag spam leads'
	},
	{
		key: 'bot',
		title: 'Block bot form submissions',
		label: 'Block bot submissions',
		// R321 (Gustavo, 13 Sep, item 1): says what the step does, not what to install.
		desc: "Every visitor's bot probability is scored before form submission is attempted."
	}
]

/* The provider tiles, the same pair `CrmStep` draws, so the mark keeps its
   ground when the picker folds into the connected card. R296 (dark audit,
   pin #92): the pastel literals were the one bright block on every dark
   surface; the --fs-tile-* inks in form-spam-page.css keep the exact hexes in
   light and take a dark tint of the same hue in dark. */
export const CRM_TILE: Record<CrmProvider, string> = {
	brevo: 'border-[rgb(var(--fs-tile-brevo-border))] bg-[rgb(var(--fs-tile-brevo-bg))]',
	hubspot: 'border-[rgb(var(--fs-tile-hubspot-border))] bg-[rgb(var(--fs-tile-hubspot-bg))]'
}

export const slugify = (value: string) =>
	value
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '')
		.slice(0, 40) || 'form'

/**
 * R280 (Esteban, 4 Sep, item 3): the `data-action` value the widget snippet
 * carries, derived from the stored selector. The wizard stores `#slug`, so
 * the slug comes straight back; the older seeds hold CSS selectors such as
 * `form.contact` or `form#ticket`, and the last id or class token is the
 * name that reads right in `data-action`.
 */
export const widgetAction = (selector: string) => slugify(selector.split(/[#.]/).pop() || selector)

/* The vertical fold (`form-spam-page.css`): the grid row measures the child,
   so a block folds open and shut over 200ms with no JavaScript measure. */
export const fold = (open: boolean) => `form-spam-collapse ${open ? 'form-spam-collapse--open' : ''}`

/**
 * The demo backend check behind the URL field. 600ms after the last keystroke
 * the line reads "Checking…", 1500ms later it settles. A URL that holds "fail"
 * demos the missing branch. `knownUrl` is a URL the host already trusts (the
 * drawer's stored URL): while the field still holds it the line reads
 * "Form detected" at once and no check runs. `active` false parks the line on
 * idle, so a closed host forgets the last answer.
 */
export const useUrlVerify = (
	url: string,
	active: boolean,
	knownUrl?: string
): VerifyState => {
	const { api } = useFormSpam()
	const [verify, setVerify] = useState<VerifyState>('idle')
	useEffect(() => {
		if (!active) {
			setVerify('idle')
			return
		}
		const value = url.trim()
		if (knownUrl !== undefined && value && value === knownUrl.trim()) {
			setVerify('detected')
			return
		}
		setVerify('idle')
		if (!value) return
		let cancelled = false
		let check: number | undefined
		const debounce = window.setTimeout(() => {
			setVerify('checking')
			if (api) {
				api
					.check(value, null)
					.then((result) => {
						if (!cancelled) setVerify(result.resolved && result.email_field_detected ? 'detected' : 'missing')
					})
					.catch(() => {
						if (!cancelled) setVerify('missing')
					})
				return
			}
			check = window.setTimeout(() => {
				setVerify(value.toLowerCase().includes('fail') ? 'missing' : 'detected')
			}, 1500)
		}, 600)
		return () => {
			cancelled = true
			window.clearTimeout(debounce)
			if (check) window.clearTimeout(check)
		}
	}, [url, active, knownUrl, api])
	return verify
}

/**
 * R272 (Esteban, 4 Sep): "there should be a red text or indicator in the
 * specific container and thing that tells you what needs to be set up". One
 * 12px danger line with the alert mark: under a pane subtitle (the
 * container) and right above the widget step (the thing). Danger ink, not
 * warning: the form takes no protection until this is done, and the drawer
 * badge already carries the softer warning tone. It enters and leaves over
 * 150ms; the last text holds through the exit, so the line never blanks
 * before it fades. No text renders nothing, which keeps the wizard as it was.
 */
export const SetupWarn = ({ text }: { text?: string | null }) => {
	const last = useRef(text ?? '')
	if (text) last.current = text
	const motion = useEnterExit(!!text, { duration: 150 })
	if (!motion.mounted) return null
	return (
		<p
			className={`flex items-start gap-1.5 text-[12px] font-medium leading-[16px] text-[rgb(var(--fs-danger-ink))] transition duration-150 motion-reduce:transition-none ${
				motion.entered ? 'translate-y-0 opacity-100' : '-translate-y-0.5 opacity-0'
			}`}
		>
			<AlertCircle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
			<span>{last.current}</span>
		</p>
	)
}

/**
 * The pane header: title and subtitle, and, R272, an optional red setup line
 * under the subtitle. R237: the corner X is gone, so nothing clears the corner.
 * R313 (Gustavo pin #89): the substep's master switch no longer sits on the
 * title line; it lives in the `MasterRow` right under this header.
 *
 * R334 (Esteban, 14 Sep, item 2): "let's normalize all step headers like 'Form
 * details' 'Block spammers from seeing my PPC Ads' etc to be a bigger canonical
 * since it's like the main step, and it's the same size as the others". The
 * title was 15px semibold, SMALLER than the 16px card heads under it, so the
 * step read as a caption over its own cards. It takes the platform's `xl` step
 * (18px/28px, tailwind.config.js), the same size as the wizard's cards-modal
 * title, which makes it the biggest text in the pane. The subtitle goes with
 * it, 13px/18px to 14px/20px, the level every card helper already uses.
 */
export const PaneHeader = ({
	title,
	subtitle,
	warn
}: {
	title: string
	subtitle: string
	/** R272: the red setup line under the subtitle; absent or null prints none. */
	warn?: string | null
}) => (
	<div className="flex min-w-0 flex-col gap-1">
		<h3 className="text-[18px] font-semibold leading-[28px] cg-text-text-primary">{title}</h3>
		<p className="text-[14px] leading-[20px] cg-text-text-secondary">{subtitle}</p>
		<SetupWarn text={warn} />
	</div>
)

/**
 * R313 (Gustavo pin #89): the copy of the master row under each pane header.
 * `name` is Gustavo's short column name from the forms table (R294, pin #72:
 * "Block bots", "Protect Ads", "Flag on CRM"). R320 (Esteban, 11 Sep): the
 * name no longer prints in the row, it only labels the switch for assistive
 * tech. The pane header right above already says what the protection does,
 * so a row that repeated "Block bots" and "every visitor gets a bot score"
 * read twice. R321 (Gustavo, 13 Sep): the R320 default title is gone too;
 * every row carries one state line that says only what the switch changes.
 */
const MASTER: Record<SubKey, { name: string }> = {
	email: { name: 'Protect Ads' },
	crm: { name: 'Flag on CRM' },
	bot: { name: 'Block bots' }
}

/**
 * R321 (Gustavo, 13 Sep): the two states every master row shares, one copy for
 * all three protections.
 *
 * R334 (Esteban, 14 Sep, item 1): "in the container inside of the toggle thing,
 * let's make it have a header 'Protective action enabled' and subtitle the
 * 'Complete the implementation steps below' or whatever - something generic
 * like this for all". R321's one sentence carried the state and the next move
 * in the same breath; the state is now the head and the next move the subtitle,
 * so the row reads at a glance and the head sits on the card-head level (L2)
 * with the subtitle on the helper level (L3).
 */
const MASTER_STATE: Record<'on' | 'off', { head: string; sub: string }> = {
	on: { head: 'Protective action enabled', sub: 'Complete the implementation steps below.' },
	off: { head: 'Protective action disabled', sub: 'You can enable it later for this form.' }
}

/**
 * R313 (Gustavo pin #89): master switch moved from the header into a framed row.
 * The row sits under the pane header and above the pane body: the state at the
 * left (R321, one copy for all three), the DS `Toggle` at the right. The frame
 * is the DS `Surface` the R311 sensitivity box uses (primary fill, hairline
 * border), so light and dark come from the tokens. The two states share one
 * grid cell, both mounted; the live one fades in with a 2px lift over 200ms
 * while the other fades out, so the row never changes height and the text never
 * hard-cuts. A plain row, not the DS `Toggle` `label` and `description` slots:
 * the DS `description` is a `string`, so the crossfade cannot live inside it
 * (DS gap, reported).
 *
 * R334 (Esteban, 14 Sep, item 1): each state is a head and a subtitle, not one
 * sentence ("let's make it have a header 'Protective action enabled' and
 * subtitle the 'Complete the implementation steps below'"). Both states still
 * hold TWO lines in the SAME grid cell, so the row's height is the same in both
 * and the crossfade stays a crossfade. The sides go from 12px to 14px so the
 * head starts on the same left edge as every `SettingsCard` head below it
 * (`px-3.5`), and the 12px top and bottom keep the toggle centred against the
 * two lines.
 */
const MasterRow = ({
	copy,
	on,
	disabled,
	onToggle
}: {
	/** The switch's accessible name; the row prints the state, not this. */
	copy: { name: string }
	on: boolean
	disabled?: boolean
	onToggle: (next: boolean) => void
}) => {
	// R334: one layer per state, each a head over a subtitle. The two layers
	// stack in one grid cell, so the row's height is the taller of the two (they
	// are the same two lines) and neither state can push the pane around.
	const layer = (active: boolean) =>
		`col-start-1 row-start-1 transition duration-200 motion-reduce:transition-none ${
			active ? 'translate-y-0 opacity-100' : 'pointer-events-none -translate-y-0.5 opacity-0'
		}`
	const state = (key: 'on' | 'off') => (
		<div className={layer(key === (on ? 'on' : 'off'))} aria-hidden={key !== (on ? 'on' : 'off')}>
			<p className="text-[16px] font-medium leading-[22px] cg-text-text-primary">{MASTER_STATE[key].head}</p>
			<p className="text-[14px] leading-[20px] cg-text-text-secondary">{MASTER_STATE[key].sub}</p>
		</div>
	)
	return (
		<Surface bg="primary" radius="lg" bordered className="flex items-center gap-3 px-3.5 py-3">
			<div className="grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)]">
				{state('on')}
				{state('off')}
			</div>
			<Toggle
				size="md"
				aria-label={copy.name}
				checked={on}
				disabled={disabled}
				onChange={(e) => onToggle(e.target.checked)}
			/>
		</Surface>
	)
}

/**
 * A substep body under a switch that is OFF: dimmed to 40% and dead to the
 * pointer, over 200ms both ways. The controls inside also take `disabled`
 * where they have it, so the keyboard cannot reach a dimmed row.
 */
export const Dim = ({ on, children }: { on: boolean; children: ReactNode }) => (
	<div
		aria-disabled={!on}
		className={`transition-opacity duration-200 motion-reduce:transition-none ${
			on ? 'opacity-100' : 'pointer-events-none opacity-40'
		}`}
	>
		{children}
	</div>
)

/**
 * The connected-CRM row. Provider mark, CRM name, the login's name and its
 * account id (never the email), and a "Connected" pill that opens a menu. The
 * pill is the DS secondary button with a green `StatusDot`; the menu is a DS
 * `MenuList` in a body portal, anchored under the pill's right edge with the
 * DS popover helpers, the same shape as the visitor actions menu. The portal
 * sits at z 70, over the modal's 60, so it paints on top of the sheet.
 * R265: this used to be its own card that replaced the picker. It is now the
 * connected face of a picker row (`ProviderRow`); `ConnectedCrmCard` below
 * keeps the card shape for any host that still wants it.
 * R279 (Esteban, 4 Sep, items 8, 9): the menu is an account picker first. It
 * lists every login the workspace holds, both CRMs, with the one this form
 * uses checked; a pick swaps the form's connection to that login. Under a
 * divider sit, only on a login the CRM revoked, Reconnect first (R284,
 * Esteban, 7 Sep: "reconnect should be the top action"), then New CRM and
 * Manage. A revoked login turns the pill red and reads "Access lost"; R279's
 * Reconnect beat read "Reconnecting…" on a disabled pill, then the pill went
 * back to green "Connected".
 * R312 (Esteban, 11 Sep): a click on Reconnect cannot restore the login from
 * here, the user must go to Connections, so for now the click only closes
 * the menu, the same as Manage.
 * R284 (Esteban, 7 Sep): the row's second line is about the account, not
 * the person who logged in ("it shouldn't say 'Maya Chen' ... is mostly
 * about the account"). R285 (Esteban, 7 Sep): "must also include account
 * name, let's say the name is always organizations, not people", so
 * `CrmAccount.name` is the organization and the line reads
 * "Acme Inc · ID 8841220"; the menu rows carry the same name.
 * R287 (Esteban, 8 Sep): "the dropdown gets out of the modal here". The
 * portaled menu followed the pill while the wizard body scrolled and kept
 * painting after the pill had left the body's clip. The DS `Dropdown` fits its
 * menu to the room below the trigger with `useMenuRoomFit` and closes it once
 * the trigger scrolls out of its scroll parent's view; the row now does both.
 */
export const ConnectedCrmRow = ({
	provider,
	account,
	accounts,
	disabled,
	onPick,
	onConnectAnother
}: {
	provider: CrmProvider
	account: CrmAccount
	/** R279: every login the workspace holds, both CRMs, for the picker rows. */
	accounts: CrmAccount[]
	disabled: boolean
	/** R279: the form moves to this login. Called for the checked row too. */
	onPick: (account: CrmAccount) => void
	onConnectAnother: () => void
}) => {
	const { api } = useFormSpam()
	const manage = () => {if(api) window.location.assign(`/settings/workspace/${api.accountId}/asset/${api.assetId}/connections?tab=crm`)}
	const [open, setOpen] = useState(false)
	const lost = account.status === 'lost'
	const anchorRef = useRef<HTMLDivElement>(null)
	const panelRef = useRef<HTMLDivElement>(null)
	const menuId = useId()
	const { mounted, style } = usePopoverMotion(open, { transformOrigin: 'top right' })
	// R287: the DS room fit. `ready` keys on `mounted`: the motion hook mounts
	// the portal in the same commit, so the panel is measurable on that frame.
	// The fit caps the menu to the room under the pill (at least three rows)
	// and scrolls the scroll parent to make room when there is not enough.
	const { fitHeight, scrollParentRef } = useMenuRoomFit({
		open,
		ready: mounted,
		anchorRef,
		panelRef,
		gap: PANEL_GAP
	})

	const place = useCallback(
		(strategy: Strategy) => {
			const anchor = anchorRef.current
			const panel = panelRef.current
			if (!anchor || !panel) return
			const rect = anchor.getBoundingClientRect()
			// clientWidth, not innerWidth: a fixed panel measures from the edge
			// inside the scrollbar.
			const rightInset = document.documentElement.clientWidth - rect.right
			panel.style.top = `${anchoredTop(rect.bottom + PANEL_GAP, strategy)}px`
			panel.style.right = `${anchoredRight(rightInset, strategy)}px`
			// R287: `place` re-runs on every scroll of the pill's scroll parent.
			// Once the pill has left that parent's view the menu would float
			// outside the sheet, so it closes instead (the DS `Dropdown` rule).
			const scrollParent = scrollParentRef.current
			if (scrollParent && scrollParent !== document.scrollingElement) {
				const box = scrollParent.getBoundingClientRect()
				if (rect.bottom <= box.top || rect.top >= box.bottom) setOpen(false)
			}
		},
		[scrollParentRef]
	)
	const strategy = useAnchoredPlacement({ active: mounted, anchorRef, panelRef, place })

	// Close on a click outside or on Escape. Escape is read in the CAPTURE
	// phase and stopped there, so the DS Modal under us does not close on the
	// same key press: one Escape closes the menu, the next one the sheet.
	useEffect(() => {
		if (!open) return
		const onDown = (e: MouseEvent) => {
			const target = e.target as Node
			if (anchorRef.current?.contains(target) || panelRef.current?.contains(target)) return
			setOpen(false)
		}
		const onKey = (e: KeyboardEvent) => {
			if (e.key !== 'Escape') return
			e.stopPropagation()
			setOpen(false)
			anchorRef.current?.querySelector('button')?.focus()
		}
		document.addEventListener('mousedown', onDown)
		document.addEventListener('keydown', onKey, true)
		return () => {
			document.removeEventListener('mousedown', onDown)
			document.removeEventListener('keydown', onKey, true)
		}
	}, [open])

	const run = (action: () => void) => () => {
		action()
		setOpen(false)
	}

	return (
		<div className="flex w-full items-center gap-3">
			{/* The same 40px tile `CrmRow` draws, so the mark stays put when the
			    row swaps between its two faces. */}
			<div
				className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border shadow-[0px_1px_1px_0px_rgba(16,24,40,0.05)] ${CRM_TILE[provider]}`}
			>
				{provider === 'brevo' ? <BrevoMark /> : <HubSpotMark />}
			</div>
			<div className="min-w-0 flex-1">
				<p className="truncate text-[14px] font-medium leading-[20px] cg-text-text-primary">{CRM_NAMES[provider]}</p>
				<p className="truncate text-[12px] leading-[16px] cg-text-text-secondary">
					{account.name} · ID {account.accountId}
				</p>
			</div>
			<div ref={anchorRef} className="inline-flex shrink-0">
				{/* R239 (Esteban, 3 Sep): "Can we make this a secondary button".
				    The trigger was `outline` (brand tint). Secondary is the same
				    white surface the footer buttons use.
				    R248 (Esteban, 3 Sep, Inspect edit): padding-left/right 10px → 6px,
				    gap 6px → 4px, and "make the chevron inside this button be a bit of
				    a lighter token". The DS sm size is `cg-px-s-10 cg-gap-s-6` and
				    twMerge cannot dedupe the `cg-` prefix, so the override is `!`
				    literal values. The label stays text-primary; the chevron takes
				    text-tertiary, the token every DS chevron uses (ListItem, the
				    accordion, NavItem). */}
				<AppButton
					type="button"
					variant="secondary"
					size="sm"
					className="!px-[6px] !gap-[4px]"
					label={lost ? 'Access lost' : 'Connected'}
					leftIcon={<StatusDot tone={lost ? 'danger' : 'success'} size="md" />}
					rightIcon={<ChevronDown className="cg-text-text-tertiary" />}
					selected={open}
					disabled={disabled}
					aria-haspopup="menu"
					aria-expanded={open}
					aria-controls={open ? menuId : undefined}
					onClick={() => setOpen((v) => !v)}
				/>
				{mounted &&
					createPortal(
						<div ref={panelRef} style={{ position: strategy, zIndex: 70, ...style }}>
							{/* R279: 240px → 300px. The widest supporting line, "HubSpot · ID
							    7715903 · Access lost", measures 232px; with the row padding, the
							    selected check and the menu padding it needs 288px, so 280 still
							    cut it to "Access l…". */}
							<MenuList
								id={menuId}
								role="menu"
								aria-label={`${CRM_NAMES[provider]} connection`}
								className="w-[300px]"
								// R287: null until the fit has measured, then the room below the
								// pill. The DS inner scroll region takes over when the rows exceed it.
								maxHeight={fitHeight ?? undefined}
							>
								{/* R279 (Esteban, 4 Sep, item 8): one row per login the workspace
								    holds, both CRMs. The marks take no size prop, so the rows carry
								    no leading icon; the CRM name sits in the supporting line. The
								    list cannot change while the menu is open (every action closes
								    it first), so the rows need no motion of their own. */}
								{accounts.map((a) => (
									<ListItem
										key={a.id}
										as="button"
										role="menuitem"
										label={a.name}
										supportingText={`${CRM_NAMES[a.provider]} · ID ${a.accountId}${
											a.status === 'lost' ? ' · Access lost' : ''
										}`}
										selected={a.id === account.id}
										onClick={run(() => onPick(a))}
									/>
								))}
								<MenuListDivider />
								{/* R279 (Esteban, 4 Sep, item 9): only a revoked login offers
								    Reconnect. The click closed the menu before the status could
								    change, so the row never appeared or left on screen.
								    R284 (Esteban, 7 Sep): it is the first action under the divider,
								    the repair sits above the two routine actions.
								    R312 (Esteban, 11 Sep): a click on Reconnect cannot restore the
								    login from here, the user must go to Connections, so for now the
								    click only closes the menu, the same as Manage. */}
								{lost && (
									<ListItem
										as="button"
										role="menuitem"
										leadingIcon={<RefreshCcw01 />}
										label="Reconnect"
										onClick={run(manage)}
									/>
								)}
								{/* R253 (Esteban, 3 Sep): "Add icons to the left of the menu items".
								    DS ListItem leadingIcon slot (16px box, 8px gap).
								    R279: "Connect another CRM" reads "New CRM" now that the rows
								    above pick among the held logins. */}
								<ListItem
									as="button"
									role="menuitem"
									leadingIcon={<Plus />}
									label="New CRM"
									onClick={run(onConnectAnother)}
								/>
								{/* R240 (Esteban, 3 Sep): "Make 'manage' active and clickable just
								    doesn't do anything". Manage has no destination in the prototype
								    yet, so the click only closes the menu. */}
								<ListItem
									as="button"
									role="menuitem"
									leadingIcon={<Settings01 />}
									label="Manage"
									onClick={run(manage)}
								/>
							</MenuList>
						</div>,
						document.body
					)}
			</div>
		</div>
	)
}

export const ConnectedCrmCard = (props: Parameters<typeof ConnectedCrmRow>[0]) => (
	<SettingsCard>
		<ConnectedCrmRow {...props} />
	</SettingsCard>
)

const statusLayer = (active: boolean) =>
	`absolute inset-0 flex items-center gap-1.5 transition-opacity duration-200 motion-reduce:transition-none ${
		active ? 'opacity-100' : 'pointer-events-none opacity-0'
	}`

/**
 * Page 0 / the first Setup block: the URL, the name and the check line.
 * R277 (Esteban, 4 Sep, item 7): `mode="edit"` is the drawer's shape. A linked
 * form keeps its URL, so the field is read only and the idle line says so; the
 * name field is gone (the drawer header rename owns the name), so the grid is
 * one column. The wizard keeps `create`: two fields, both live.
 */
export const DetailsPane = ({
	url,
	name,
	verify,
	mode = 'create',
	onUrl,
	onName
}: {
	url: string
	name: string
	verify: VerifyState
	/** R277: `edit` = the drawer. One column, URL read only, no name field. */
	mode?: 'create' | 'edit'
	onUrl: (next: string) => void
	/** The name field's change; `edit` mode renders no name field, so none is needed. */
	onName?: (next: string) => void
}) => {
	const edit = mode === 'edit'
	return (
		<div className="flex flex-col gap-4">
			<PaneHeader title="Form details" subtitle="Tell us which form to protect." />
			{/* R218 (Esteban, 1 Sep): the helper is one full-width line under the two
			   fields; the URL field sits on the LEFT, so the helper starts under it.
			   R277: in `edit` mode the URL field stands alone, so the grid is one
			   column and the helper starts under the same left edge. */}
			<div className="flex flex-col gap-3">
				<div className={edit ? 'grid grid-cols-1 gap-4' : 'grid grid-cols-2 gap-4'}>
					<Input
						// R293 (Gustavo, 4 Sep, pins #76, #77): three words, the same shape as "Form name".
						label="Form page URL"
						size="md"
						placeholder="demo.example.com/demo"
						value={url}
						readOnly={edit}
						onChange={(e) => onUrl(e.target.value)}
						state={verify === 'missing' ? 'error' : verify === 'detected' ? 'success' : 'default'}
					/>
					{/* The mode is fixed per host, so this field never enters or leaves at
					   runtime: no motion is owed here. */}
					{!edit && (
						<Input
							label="Form name"
							size="md"
							state="default"
							placeholder="Form name"
							value={name}
							onChange={(e) => onName?.(e.target.value)}
						/>
					)}
				</div>
				<div className="relative h-[24px]" aria-live="polite">
					<div className={statusLayer(verify === 'idle')}>
						<p className="text-[14px] leading-[20px] cg-text-text-secondary">
							{edit
								? 'The URL is fixed after the form is created.'
								: 'We check the page for your form when you enter the URL.'}
						</p>
					</div>
					<div className={statusLayer(verify === 'checking')}>
						<span className="flex h-5 w-5 items-center justify-center">
							<DotMatrix size="xs" label="Checking the page" />
						</span>
						<p className="text-[14px] leading-[20px] cg-text-text-secondary">Checking…</p>
					</div>
					<div className={statusLayer(verify === 'detected')}>
						<CheckCircle className="h-5 w-5 cg-text-success" aria-hidden="true" />
						<p className="text-[14px] font-medium leading-[20px] cg-text-success">Form detected</p>
					</div>
					<div className={statusLayer(verify === 'missing')}>
						<AlertCircle className="h-5 w-5 text-[rgb(var(--fs-danger-ink))]" aria-hidden="true" />
						<p className="text-[14px] font-medium leading-[20px] text-[rgb(var(--fs-danger-ink))]">
							Form not detected
						</p>
					</div>
				</div>
			</div>
		</div>
	)
}

/**
 * Page 1: the email classes under the "Protect my ads" switch.
 *
 * R292 (Esteban, 10 Sep): "at least one of the toggles below should be on,
 * if that last one is turned off by the user, the whole thing should turn
 * off with it". The pane hands the master and the rules back as ONE value,
 * so each host writes one coupled change and neither can hold the switch on
 * over an empty list. The coupling lives in `setEmailRule` and
 * `setEmailMaster` (form-spam-data); the two callbacks this pane took before
 * (`onToggle`, `onRule`) let a host write the halves apart.
 *
 * R333 (Gustavo, 14 Sep, Cherry pin #102): the Threshold card comes first,
 * before the rules, and the rules card gets a title of its own, "Choose
 * which types of bad submissions can trigger an exclusion.", now that the
 * master row is off the card.
 */
export const EmailPane = ({
	on,
	rules,
	warn,
	threshold,
	onChange,
	onThreshold
}: {
	on: boolean
	rules: Record<string, boolean>
	/** R272: the drawer's red setup line; the wizard passes none. */
	warn?: string | null
	/** R323 (Gustavo, 13 Sep, item 1): which bad submission starts the block. */
	threshold: BlockThreshold
	onChange: (next: EmailSetting) => void
	onThreshold: (next: BlockThreshold) => void
}) => {
	const onMaster = (next: boolean) => onChange(setEmailMaster({ on, rules }, next))
	const onRule = (key: string, next: boolean) => onChange(setEmailRule({ on, rules }, key, next))
	return (
		<div className="flex flex-col gap-4">
			<PaneHeader title={CARDS[0].title} subtitle={CARDS[0].desc} warn={warn} />
			<MasterRow copy={MASTER.email} on={on} onToggle={onMaster} />
			<Dim on={on}>
				<ThresholdCard value={threshold} disabled={!on} onChange={onThreshold} />
			</Dim>
			{/* `EmailStep` dims its own list off `master`, so no `Dim` here. */}
			<EmailStep
				title="Choose which types of bad submissions can trigger an exclusion."
				showMaster={false}
				master={on}
				rules={rules}
				onMaster={onMaster}
				onRule={onRule}
			/>
		</div>
	)
}

/**
 * R323 (Gustavo, 13 Sep, item 1): the Threshold section under the email rules.
 * His line, "Block when the [first / second / third / fourth] bad submission
 * happens.", with the pick as a four-step DS `SegmentedTabs`, the control the
 * two sensitivity folds on the bot pane already use, so the three pickers of
 * the setup read as one family. The row sits in the same grey `InsetList` as
 * the rules above it, in its own `SettingsCard`, with the card sentence the
 * bot card also opens with. The note under the label reads the pick back in
 * his words; the four notes share one grid cell and crossfade on a change,
 * the way the sensitivity notes do.
 *
 * R327 (Esteban, 14 Sep): the card sentence in plain words, and the note on
 * its own full-width line under the label-and-tabs row. Beside the tabs the
 * note had 213px in the wizard, so every pick wrapped at a different word
 * and the crossfade read as a reflow; the full inset row (416px) holds all
 * four on one line.
 *
 * R333 (Gustavo, 14 Sep, Cherry pin #101): the card opens with the title
 * "Threshold" in place of the sentence, the inset holds the tabs alone with
 * no second label, the note reads "Block after the [first / second / third]
 * bad submission from the same visitor.", and the Fourth step is gone. The
 * tab list keeps `aria-label="Threshold"` since the visible label went.
 *
 * R336 (Esteban, 15 Sep, /ss of the CAPTCHA sensitivity fold): "make the
 * threshold ... be like this container, white with the gray fill". The tabs
 * and the note now sit in the same DS `Surface` box the sensitivity folds use
 * (primary fill, `md` radius, hairline border, 12px sides, 10px top and
 * bottom) inside the grey inset. The note stays on its own line under the
 * tabs (R327): beside them it would wrap in the wizard.
 */
const ThresholdCard = ({
	value,
	disabled,
	onChange
}: {
	value: BlockThreshold
	disabled: boolean
	onChange: (next: BlockThreshold) => void
}) => (
	<SettingsCard>
		<p className="text-[16px] font-medium leading-[22px] cg-text-text-primary">Threshold</p>
		<InsetList>
			<Surface bg="primary" radius="md" bordered className="flex flex-col gap-2 px-3 py-2.5">
				<SegmentedTabs
					size="small"
					activeTone="brand"
					className="w-fit"
					value={String(value)}
					onChange={(next) => onChange(Number(next) as BlockThreshold)}
				>
					<SegmentedTabList aria-label="Threshold">
						{BLOCK_THRESHOLDS.map((t) => (
							<SegmentedTab key={t.key} value={String(t.key)} label={t.label} disabled={disabled} />
						))}
					</SegmentedTabList>
				</SegmentedTabs>
				<div className="grid grid-cols-[minmax(0,1fr)]">
					{BLOCK_THRESHOLDS.map((t) => (
						<p
							key={t.key}
							aria-hidden={t.key !== value}
							className={`col-start-1 row-start-1 text-[12px] leading-[16px] cg-text-text-secondary transition duration-200 motion-reduce:transition-none ${
								t.key === value ? 'translate-y-0 opacity-100' : 'pointer-events-none -translate-y-0.5 opacity-0'
							}`}
						>
							Block after the {t.label.toLowerCase()} bad submission from the same visitor.
						</p>
					))}
				</div>
			</Surface>
		</InsetList>
	</SettingsCard>
)

/**
 * One CRM row of the picker. R265 (Esteban, 4 Sep): "can we make hubspot be
 * 'connected' and brevo be 'disconnected'", so both looks show at once. The
 * picker no longer folds into a separate connected card: each row carries
 * its own state. Connected, it shows the login's name and account id and the
 * Connected pill with its menu (`ConnectedCrmRow`); otherwise the Connect
 * button (`CrmRow`). The two faces swap in one grid cell over 200ms, each
 * held through its fade, so a login that lands or a "Connect another CRM"
 * never hard-cuts. Both faces stand on the same 40px tile, so the card keeps
 * its height. R247: Connect opens the OAuth popup at once, inside the click
 * gesture, so `onConnect` runs straight from the click handler. R283: once a
 * CRM connects, the other CRM's row folds away (`FoldRow` in `CrmPane`), so
 * the two looks no longer stand side by side.
 */
const ProviderRow = ({
	provider,
	account,
	accounts,
	state,
	disabled,
	onConnect,
	onPick,
	onConnectAnother
}: {
	provider: CrmProvider
	account: CrmAccount | null
	/** R279: every held login, for the connected face's picker menu. */
	accounts: CrmAccount[]
	state: CrmState
	disabled: boolean
	onConnect: () => void
	onPick: (account: CrmAccount) => void
	onConnectAnother: () => void
}) => {
	const { api } = useFormSpam()
	const held = useRef<CrmAccount | null>(account)
	if (account) held.current = account
	const linked = useEnterExit(!!account, { duration: 200 })
	const picker = useEnterExit(!account, { duration: 200 })
	const fade = (entered: boolean) =>
		`col-start-1 row-start-1 transition-opacity duration-200 motion-reduce:transition-none ${
			entered ? 'opacity-100' : 'pointer-events-none opacity-0'
		}`
	return (
		<div className="grid grid-cols-[minmax(0,1fr)]">
			{picker.mounted && (
				<div className={fade(picker.entered)} aria-hidden={!picker.entered}>
					<CrmRow
						tile={provider === 'brevo' ? <BrevoMark /> : <HubSpotMark />}
						tileClass={CRM_TILE[provider]}
						name={CRM_NAMES[provider]}
						action={api && provider === 'brevo' ? 'Coming soon' : 'Connect'}
						disabled={disabled || (!!api && provider === 'brevo')}
						accounts={api && provider === 'hubspot' ? accounts.filter(a => a.provider === provider) : undefined}
						onPickAccount={id => {const selected = accounts.find(a => a.id === id); if(selected) onPick(selected)}}
						onConnectNew={onConnect}
						state={state}
						onAction={onConnect}
					/>
				</div>
			)}
			{linked.mounted && held.current && (
				<div className={fade(linked.entered)} aria-hidden={!linked.entered}>
					<ConnectedCrmRow
						provider={provider}
						account={held.current}
						accounts={accounts}
						disabled={disabled}
						onPick={onPick}
						onConnectAnother={onConnectAnother}
					/>
				</div>
			)}
		</div>
	)
}

/**
 * R283 (Esteban, 7 Sep): "there should only be one CRM integration at a
 * time". Every picker row rides this vertical fold. With no connection the
 * card lists every CRM with Connect and the Request row. When a login lands,
 * the other CRM's row, the Request row and the dividers between them fold
 * away over 200ms and the connected row stands alone; they fold back in when
 * the connection clears (New CRM). `useEnterExit` holds a row mounted through
 * its exit, so nothing hard-cuts.
 */
const FoldRow = ({ show, children }: { show: boolean; children: ReactNode }) => {
	const io = useEnterExit(show, { duration: 200 })
	if (!io.mounted) return null
	return (
		<div className={fold(io.entered)} aria-hidden={!io.entered}>
			<div>{children}</div>
		</div>
	)
}

/**
 * Page 2: the CRM. With no connection the picker lists every CRM; a
 * connection folds the list down to the one connected row (R283), and the
 * field card rides its own fold under it. `connected` is the ONE CRM the
 * form uses (R151: one per form) with the login it uses; null shows the picker.
 * The host owns the popup flow (`useCrmConnect`) and the field check
 * (`useCrmField`); this pane owns only the folds and the held card.
 */
export const CrmPane = ({
	on,
	onToggle,
	crm,
	onConnect,
	request,
	onRequest,
	connected,
	accounts,
	onPick,
	onConnectAnother,
	fieldState,
	onCheckField
}: {
	on: boolean
	onToggle: (next: boolean) => void
	/** The popup flow's transient state per CRM. */
	crm: Record<CrmProvider, CrmState>
	onConnect: (provider: CrmProvider) => void
	request: RequestState
	/** Opens the request dialog. */
	onRequest: () => void
	connected: { provider: CrmProvider; account: CrmAccount } | null
	/** R279 (Esteban, 4 Sep, items 8, 9): the picker menu's rows and actions. */
	accounts: CrmAccount[]
	onPick: (account: CrmAccount) => void
	onConnectAnother: () => void
	fieldState: CrmFieldState
	onCheckField: () => void
}) => {
	const provider = connected?.provider ?? null
	const cardIO = useEnterExit(!!provider, { duration: 200 })

	// The picker row never reads `connected` off the popup state: the
	// connected face comes from `connected` (`ProviderRow`). Only the busy and
	// the blocked-popup states pass through.
	const rowState = (key: CrmProvider) =>
		crm[key] === 'connecting' || crm[key] === 'blocked' ? crm[key] : 'idle'
	const row = (key: CrmProvider) => (
		<ProviderRow
			provider={key}
			account={connected?.provider === key ? connected.account : null}
			accounts={accounts}
			state={rowState(key)}
			disabled={!on}
			onConnect={() => onConnect(key)}
			onPick={onPick}
			onConnectAnother={onConnectAnother}
		/>
	)

	return (
		<div className="flex flex-col gap-4">
			<PaneHeader title={CARDS[1].title} subtitle={CARDS[1].desc} />
			<MasterRow copy={MASTER.crm} on={on} onToggle={onToggle} />
			{/* R293 (Gustavo, 4 Sep, pin #91): the `Dim` left this block for the two
			    provider rows and the field card. The request row is not a protection;
			    it asks us for a CRM we do not have, and that ask stands while the
			    switch is off. */}
			<div className="flex flex-col gap-4">
				<div>
						{/* R242 (Esteban, 3 Sep): "Let's not have 'step 1: connect' or step 2,
						    that's just slop you added on". The two small heads are gone; the
						    CRM picker and the field card stand on their own. R265: the picker
						    stays put; a connected CRM shows on its own row (`ProviderRow`).
						    The `pb-px` keeps the card's 1px shadow out of the fold clip. */}
						<div className="pb-px">
							<SettingsCard>
								{/* R283: the options list. A connected CRM keeps its row; the
								    rest fold away (`FoldRow`). ONE gapless child: the card's own
								    `gap-3` would stay around a folded row until it unmounts and
								    then snap shut, so the 12px above and below each divider lives
								    inside the divider's fold and leaves with it. */}
								<div className="flex flex-col">
								<Dim on={on}>
								<FoldRow show={!provider || provider === 'brevo'}>{row('brevo')}</FoldRow>
								<FoldRow show={!provider}>
									<div className="py-3">
										<Divider />
									</div>
								</FoldRow>
								<FoldRow show={!provider || provider === 'hubspot'}>{row('hubspot')}</FoldRow>
								</Dim>
								<FoldRow show={!provider}>
									<div className="py-3">
										<Divider />
									</div>
								</FoldRow>
								<FoldRow show={!provider}>
									<CrmRow
										tile={<AnnotationPlus className="cg-text-brand h-6 w-6" aria-hidden="true" />}
										name="Request a new CRM integration"
										action="Request"
										state={
											request === 'sending'
												? 'connecting'
												: request === 'sent'
													? 'connected'
													: 'idle'
										}
										busyLabel="Sending…"
										doneLabel="Request sent"
										onAction={onRequest}
									/>
								</FoldRow>
								</div>
							</SettingsCard>
						</div>
						{/* R238 (Esteban, 3 Sep): "I should not see this here, this is just
						    slop you added on". The waiting line under a Step 2 head is gone.
						    Step 2 exists only once a CRM is connected: it rides the same
						    `cardIO` fold as the connected card, and its 16px gap lives inside
						    the fold, so nothing is left behind while it is absent. Step 1 and
						    this fold share ONE flex child for the same reason. R242: the Step 2
						    head went too; only the field card rides the fold. */}
						{cardIO.mounted && (
							<Dim on={on}>
							<div className={fold(cardIO.entered)} aria-hidden={!cardIO.entered}>
								<div>
									<div className="pt-4">
										<CrmFieldPanel provider={provider} state={fieldState} onCheck={onCheckField} />
									</div>
								</div>
							</div>
							</Dim>
						)}
					</div>
				</div>
		</div>
	)
}

/**
 * R278 (Esteban, 4 Sep, item 5) built ONE Normal / Strict row under the inset,
 * for both switches. R309 (Gustavo pin #81, Esteban 11 Sep): one fold PER
 * switch, and each one sits INSIDE its own switch row group, "so you know which
 * is for which". The CAPTCHA fold acts at 60% (Normal) or 40% (Strict),
 * Gustavo's thresholds; the block fold keeps 90% / 80%. Two record fields,
 * `captchaSensitivity` and `botSensitivity`, never `captchaTrigger`: the drawer
 * derives the switches from `botAction` + `captchaTrigger` (R262), so a value
 * written there would flip a switch. Built here, not on `BotTargetRow` in the
 * shared file: the legacy `BotStep` still reaches that one.
 *
 * "They only appear if the item's toggled, if not it gets hidden": the DS
 * `Collapse` folds the row in when its switch goes on and out when it goes off
 * (height + opacity, 200ms, reduced motion honoured by the DS). The 12px above
 * the fold lives INSIDE it, so the closed height is a true 0 and the inset's
 * 12px on both sides of the Divider do not move. The layout has to reflow
 * here: the fold sits between its row and the Divider.
 *
 * The fold mirrors the `ToggleRow` shape: text left, control right, so the
 * segments line up under the switch. `Collapse` clips its own box, so the
 * segment focus ring needs room at the sides: the wrapper takes 8px out and
 * the content puts 8px back (the `BotTargetRow` trick).
 *
 * R311 (Esteban, 11 Sep, on the R309 crop): "the sensitivity thing should be
 * another smaller container, it's just that there's too much text close
 * together". The fold's content now sits in its own DS `Surface` (primary
 * fill, `md` radius, hairline border, no shadow): the `SettingsCard` language
 * one step down, 10px card > 8px inset > 6px box, so the four lines of 12px
 * text no longer run together on one grey field. The box takes 12px sides
 * and 10px top and bottom, and the 12px above it lives inside the fold as
 * before. The track keeps the DS `surface-2` fill: on the white box it reads
 * as a control again, so the R309 surface override is gone.
 *
 * The block label is pin #82's placeholder ("Label would be TBD"), Esteban's
 * to change. The R278 asterisk footnote is gone: the note sits under its label.
 */
const SensitivityFold = ({
	open,
	label,
	notes,
	value,
	disabled,
	onChange
}: {
	open: boolean
	label: string
	/** R321 (Gustavo, 13 Sep, items 6 and 8): one note per level. The note
	    under the label states the threshold the picked level uses, so the
	    two notes share one grid cell and crossfade when the level changes. */
	notes: Record<BotSensitivity, string>
	value: BotSensitivity
	disabled: boolean
	onChange: (next: BotSensitivity) => void
}) => (
	<Collapse open={open} className="-mx-2">
		<div className="px-2 pt-3">
			<Surface bg="primary" radius="md" bordered className="flex items-center justify-between gap-3 px-3 py-2.5">
				<div className="min-w-0 flex-1">
					<p className="text-[12px] font-medium leading-[16px] cg-text-text-secondary">{label}</p>
					<div className="grid grid-cols-[minmax(0,1fr)]">
						{BOT_SENSITIVITIES.map((s) => (
							<p
								key={s.key}
								aria-hidden={s.key !== value}
								className={`col-start-1 row-start-1 text-[12px] leading-[16px] cg-text-text-secondary transition duration-200 motion-reduce:transition-none ${
									s.key === value ? 'translate-y-0 opacity-100' : 'pointer-events-none -translate-y-0.5 opacity-0'
								}`}
							>
								{notes[s.key]}
							</p>
						))}
					</div>
				</div>
				<SegmentedTabs
					size="small"
					activeTone="brand"
					className="w-fit shrink-0"
					value={value}
					onChange={(next) => onChange(next as BotSensitivity)}
				>
					<SegmentedTabList aria-label={label}>
						{BOT_SENSITIVITIES.map((s) => (
							<SegmentedTab key={s.key} value={s.key} label={s.label} disabled={disabled} />
						))}
					</SegmentedTabList>
				</SegmentedTabs>
			</Surface>
		</div>
	</Collapse>
)

/**
 * Page 3: the two bot switches and the widget step. R277 (Esteban, 4 Sep, item
 * 6): the R49 consent row under the switches is gone; a switch writes at once.
 * Two independent switches on the page, ONE level and ONE trigger in the store;
 * the host maps between them (the wizard: block wins; the drawer: both on =
 * `block` + `suspected`, see `form-detail-drawer.tsx`).
 */
export const BotPane = ({
	on,
	onToggle,
	captchaOn,
	blockOn,
	onCaptcha,
	onBlock,
	sensitivity,
	onSensitivity,
	captchaSensitivity,
	onCaptchaSensitivity,
	action,
	widgetState,
	onCheckWidget,
	deferVerification = false,
	warn,
	widgetMissing
}: {
	on: boolean
	onToggle: (next: boolean) => void
	captchaOn: boolean
	blockOn: boolean
	onCaptcha: (next: boolean) => void
	onBlock: (next: boolean) => void
	/** R278: Normal / Strict for the block switch, its own record field
	    (`botSensitivity`). R309: it folds in under the block row. */
	sensitivity: BotSensitivity
	onSensitivity: (next: BotSensitivity) => void
	/** R309 (Gustavo pin #81): Normal / Strict for the CAPTCHA switch, its own
	    field (`captchaSensitivity`), folded in under the CAPTCHA row. */
	captchaSensitivity: BotSensitivity
	onCaptchaSensitivity: (next: BotSensitivity) => void
	/** R280: the `data-action` value, no `#`. */
	action: string
	widgetState: WidgetCheckState
	deferVerification?: boolean
	onCheckWidget: () => void
	/** R272: the drawer's red setup line under the header; the wizard passes none. */
	warn?: string | null
	/** R272: the record says the widget is absent; a red line marks the widget step. */
	widgetMissing?: boolean
}) => (
	<div className="flex flex-col gap-4">
		<PaneHeader title={CARDS[2].title} subtitle={CARDS[2].desc} warn={warn} />
		<MasterRow copy={MASTER.bot} on={on} onToggle={onToggle} />
		<Dim on={on}>
			<div className="flex flex-col gap-4">
				<SettingsCard>
					{/* R321 (Gustavo, 13 Sep, item 2): one line; the scoring sentence
					    now lives in the pane subtitle. */}
					<p className="text-[14px] leading-[20px] cg-text-text-secondary">
						Choose how you want to mitigate suspicious bot behavior.
					</p>
					{/* R254 (Esteban, 3 Sep): the two switches sit in the same grey inset
					    as the email rules on the Protect my ads pane (shared `InsetList`).
					    R309 (Gustavo pin #81, Esteban 11 Sep): each switch owns a Normal /
					    Strict fold INSIDE its own row group, shown only while that switch
					    is on. R278's one shared row under the inset is gone. A group is a
					    gapless column, so a closed fold (height 0) adds no gap of its own
					    and the inset's 12px on both sides of the Divider stay put. R311:
					    the fold is its own white `Surface` box inside the grey inset. */}
					<div className="flex flex-col">
						<InsetList>
						<div className="flex flex-col">
							{/* R277 (Esteban, 4 Sep, item 6): no consent box; a switch writes at once. */}
							{/* R321 (Gustavo, 13 Sep, items 5 and 7): the two switch rows lose
							    their descriptions; the threshold note under each fold says
							    what the level does. */}
							<ToggleRow
								label="Show a CAPTCHA to suspicious visitors"
								checked={captchaOn}
								disabled={!on}
								onChange={onCaptcha}
							/>
							<SensitivityFold
								open={captchaOn}
								label="CAPTCHA sensitivity"
								notes={{
									normal: 'Challenge visitors with a bot probability of 60% or more.',
									strict: 'Challenge visitors with a bot probability of 40% or more.'
								}}
								value={captchaSensitivity}
								disabled={!on || !captchaOn}
								onChange={onCaptchaSensitivity}
							/>
						</div>
						<Divider />
						<div className="flex flex-col">
							<ToggleRow
								label="Block high-probability bots"
								checked={blockOn}
								disabled={!on}
								onChange={onBlock}
							/>
							<SensitivityFold
								open={blockOn}
								label="Blocking sensitivity"
								notes={{
									normal: 'Block form submissions when bot probability is 90% or more.',
									strict: 'Block form submissions when bot probability is 80% or more.'
								}}
								value={sensitivity}
								disabled={!on || !blockOn}
								onChange={onSensitivity}
							/>
						</div>
						</InsetList>
						{/* R322 (Gustavo, 13 Sep, item 4 of the pane list): with both
						    switches off the pane still scores, and this line says so. The
						    DS Collapse folds it in under the inset. The inset and the fold
						    share one gapless column, so a closed fold leaves no gap of its
						    own in the card. `open` reads the master too (Gustavo: hidden
						    when the general toggle is off), so the fold never sits open
						    behind the master's Dim. */}
						<Collapse open={on && !captchaOn && !blockOn}>
							<p className="pt-3 text-[12px] leading-[16px] cg-text-text-secondary">
								Both protective actions are disabled, but bot probability will still be scored. You can
								monitor it and enable them later.
							</p>
						</Collapse>
					</div>
				</SettingsCard>
				{/* The widget is the only part the visitor meets. Nothing this page
				    decides reaches anybody until the snippet is on the page. R272:
				    the red line above the step marks it while the record says the
				    widget is absent; the wrapper's gap-2 folds away with the line. */}
				<div className="flex flex-col gap-2">
					<SetupWarn text={widgetMissing ? SETUP_WARN.widgetStep : null} />
					{deferVerification ? <p className="text-sm cg-text-text-secondary">
						Save this form to finish setup. Install its widget in the named form, open the normal signup flow,
						then use Verify in Setup to confirm it loaded. Installation is pending until that check succeeds.
					</p> : <WidgetStep action={action} state={widgetState} onCheck={onCheckWidget} />}
				</div>
			</div>
		</Dim>
	</div>
)

/**
 * The discard ask. R264: the wizard's Discard button and its Escape route
 * both come here; the legacy modal imports it too. Once a click used to drop
 * the whole draft without a word. Same siting rules as RequestSourceDialog: a SIBLING of the
 * wizard card (a transformed ancestor would trap the fixed overlay), and
 * zIndex 70 over the wizard's 60.
 */
export const DiscardDraftDialog = ({
	open,
	onClose,
	onConfirm
}: {
	open: boolean
	onClose: () => void
	onConfirm: () => void
}) => {
	const modal = useEnterExit(open, { duration: 200 })
	if (!modal.mounted) return null
	return (
		<Modal
			open
			onClose={onClose}
			size="md"
			rounded="xl"
			zIndex={70}
			className={`${CONFIRM_MODAL_WIDTH} !animate-none transition-[opacity,transform] duration-200 motion-reduce:transition-none ${
				modal.entered ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
			}`}
			overlayClassName={`!animate-none transition-opacity duration-200 motion-reduce:transition-none ${
				modal.entered ? 'opacity-100' : 'opacity-0'
			}`}
		>
			<div className="flex flex-col gap-5 p-6">
				<DialogHeader
					title="Discard this form?"
					subtitle="The name, the page URL and every setting you chose go away. Nothing is linked."
					onClose={onClose}
				/>
				<div className="flex justify-end gap-3">
					<AppButton
						type="button"
						variant="secondary"
						size="md"
						label="Keep editing"
						onClick={onClose}
					/>
					<AppButton
						type="button"
						variant="destructive"
						size="md"
						label="Discard"
						onClick={onConfirm}
					/>
				</div>
			</div>
		</Modal>
	)
}
